import { useEffect, useState } from 'react'
import { CalendarClock, Loader2, Play } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Badge } from '../../lib/shadcn/badge'
import { toast } from '../../lib/shadcn/sonner'
import { useGetScheduledJobs, useRunScheduledJob } from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'
import { timeAgo } from '../../utils/orderImport'

interface JobRun {
  run_key: string
  trigger: string
  status: 'running' | 'succeeded' | 'failed'
  attempt: number
  started_at: string
  finished_at: string | null
  next_retry_at: string | null
  requested_by: string | null
  error: string | null
}

interface Job {
  name: string
  label: string
  schedule: string
  maxAttempts: number
  lastRun: JobRun | null
  lastSuccessAt: string | null
  requestedAt: string | null
}

interface JobsData {
  enabled: boolean
  timeZone: string
  jobs: Job[]
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

function StatusBadge({ job }: { job: Job }) {
  if (job.requestedAt) return <Badge variant="secondary">Queued</Badge>
  const run = job.lastRun
  if (!run) return <Badge variant="outline">Not run yet</Badge>
  if (run.status === 'running') return <Badge variant="secondary">Running</Badge>
  if (run.status === 'succeeded') return <Badge variant="success">OK</Badge>
  return <Badge variant={run.next_retry_at ? 'warning' : 'destructive'}>{run.next_retry_at ? 'Retrying' : 'Failed'}</Badge>
}

// Settings > Scheduled jobs: what the server runs on its own, when each last
// worked, and a Run now button.
export default function ScheduledJobsSettings() {
  const getJobs = useGetScheduledJobs()
  const runJob = useRunScheduledJob()
  const [queuing, setQueuing] = useState<string | null>(null)
  const data = getJobs.data as JobsData | null

  const refresh = () => getJobs.trigger({}, { skipCache: true })

  useEffect(() => {
    void refresh()
    // Keep the card current while it's open: Run now finishes within a minute or two.
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, 20 * 1000)
    return () => window.clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runNow = async (job: Job) => {
    setQueuing(job.name)
    try {
      await runJob.trigger({ job: job.name })
      toast.success(`${job.label} will run within a minute`)
      await refresh()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not queue the job')
    } finally {
      setQueuing(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="w-4 h-4" />
          Scheduled jobs
        </CardTitle>
        <CardDescription><HelpText>
          The server runs these on its own, Lubbock time, even when nobody has the Hub open. A
          weekly or quarterly job that fails tries again a few times on its own; the frequent
          ones just try again next time. Run now does it straight away, without sending or
          creating anything twice.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {data && !data.enabled && (
          <p className="rounded-md border border-warning bg-warning/10 p-3 text-sm">
            Scheduled jobs are off on this server. Set <code>SCHEDULER_ENABLED=true</code> to turn them on.
          </p>
        )}
        {getJobs.error && <p className="text-sm text-destructive">{getJobs.error}</p>}
        {!data && getJobs.loading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        )}

        {data?.jobs.map(job => {
          const run = job.lastRun
          const failed = run?.status === 'failed'
          return (
            <div key={job.name} className="rounded-md border p-3 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{job.label}</span>
                <StatusBadge job={job} />
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto gap-1.5"
                  disabled={!data.enabled || !!job.requestedAt || run?.status === 'running' || queuing === job.name}
                  onClick={() => void runNow(job)}
                >
                  {queuing === job.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Run now
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">{job.schedule}</p>
              <p className="text-sm">
                <span className="text-muted-foreground">Last worked: </span>
                {job.lastSuccessAt ? `${timeAgo(job.lastSuccessAt)} (${when(job.lastSuccessAt)})` : 'never'}
              </p>
              {run && run.status !== 'succeeded' && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Latest try: </span>
                  {when(run.started_at)}
                  {job.maxAttempts > 1 && ` · attempt ${run.attempt} of ${job.maxAttempts}`}
                  {run.next_retry_at && ` · tries again ${when(run.next_retry_at)}`}
                </p>
              )}
              {failed && run.error && (
                <pre className="whitespace-pre-wrap rounded bg-destructive/10 p-2 text-xs text-destructive">{run.error}</pre>
              )}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
